import React from 'react';
import { useQuery } from '@apollo/react-hooks';
import { Container, Table, Row, Col, Card } from 'react-bootstrap';

import { IProps } from './types';
import { styled } from '@styles';

import Button from '@src/componentsNew/Buttons/Button';
import { SimplePricePreview } from '@atoms/SimplPricePreview';

import { POINT_RATE } from '@components/organisms/CheckoutSummary/queries';
import { pointRate } from '@components/organisms/CheckoutSummary/__generated__/pointRate';

import { getPrice, getPriceWithFraction } from '@src/core/utils';

const CheckoutCard = styled(Card)`
  margin-bottom: 1rem;
`;

const CheckoutHeader = styled(Card.Header)``;

const CheckoutBody = styled(Card.Body)``;

const OrderSummary = styled.p`
  font-weight: 700;
  text-align: center;
  text-transform: uppercase;
  margin: 0;
`;

const Td = styled.td``;

const Discount = styled.td`
  color: #ff3398;
`;

const TableItemRight = styled.td`
  text-align: right;
`;

const TableItemRightPoint = styled.td`
  text-align: right;
  color: #2983ff;
`;

const TableItemRightDiscount = styled.td`
  text-align: right;
  color: #ff3398;
`;

const Hr = styled.hr`
  border-top: 1px solid black;
`;

const ContinueButtonWrapperCol = styled(Col)`
  @media (min-width: 576px) {
    max-width: 540px;
  }

  @media (min-width: 768px) {
    max-width: 720px;
  }

  @media (max-width: 767px) {
    position: fixed;
    left: 50%;
    transform: translateX(-50%);
    bottom: 0;
    width: 100%;
    padding: 60px 16px 24px;
    background: #ffffff;
    z-index: 1;
  }

  .continue-btn {
    width: 100%;
    height: 46px;
    border-radius: 30px;
    color: #ffffff;
    background-color: #f82296;

    &:hover {
      background-color: #d60777;
    }
  }
`;

const CheckoutSummary: React.FC<IProps> = ({
  originalTotal,
  codAmount,
  shippingPrice,
  usedPoint,
  discountAmount,
  totalPrice,
  voucherCode,
  handleClickContinue,
  grandTotal,
  disabled,
  estimatedEarnPoint,
}: IProps) => {
  const [pointRateValue, setPointRateValue] = React.useState(0);

  const { data, error } = useQuery<pointRate>(POINT_RATE, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      setPointRateValue(data.shop.pointRatioForOrder);
    },
  });

  return (
    <div>
      <CheckoutCard style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.125)' }}>
        <CheckoutHeader>
          <OrderSummary>Order Overview</OrderSummary>
        </CheckoutHeader>
        <CheckoutBody>
          <Container>
            <Table borderless={true} size="sm">
              <tbody>
                <tr>
                  <Td>Subtotal</Td>
                  <TableItemRight>{getPriceWithFraction(originalTotal.gross.amount, true, true)}</TableItemRight>
                </tr>
                {codAmount.amount > 0 ? (
                  <tr>
                    <Td>Total COD Charge</Td>
                    <TableItemRight>{getPriceWithFraction(codAmount.amount, true, true)}</TableItemRight>
                  </tr>
                ) : (
                  <></>
                )}
                {originalTotal.gross.amount - totalPrice.gross.amount > 0 ? (
                  <tr>
                    <Discount>Discount</Discount>
                    <TableItemRightDiscount>
                      -{getPriceWithFraction(originalTotal.gross.amount - totalPrice.gross.amount, true, true)}
                    </TableItemRightDiscount>
                  </tr>
                ) : null}
                <tr>
                  <Td>Deliver Charge</Td>
                  <TableItemRight>{getPrice(shippingPrice.amount, true)}</TableItemRight>
                </tr>
                {usedPoint.amount > 0 ? (
                  <tr>
                    <Discount>Point</Discount>
                    <TableItemRightDiscount>-{getPrice(usedPoint.amount, true)}</TableItemRightDiscount>
                  </tr>
                ) : (
                  <></>
                )}
                {discountAmount.amount > 0 && voucherCode ? (
                  <>
                    <tr>
                      <Discount>Voucher Amount</Discount>
                      <TableItemRightDiscount>-{getPrice(discountAmount.amount, true)}</TableItemRightDiscount>
                    </tr>
                    <tr>
                      <Discount>Voucher Code</Discount>
                      <TableItemRightDiscount>{voucherCode}</TableItemRightDiscount>
                    </tr>
                  </>
                ) : (
                  <></>
                )}
              </tbody>
            </Table>
            <Hr />
            <Table borderless={true} size="sm">
              <tbody>
                <tr>
                  <td>Total</td>
                  <TableItemRight>{getPriceWithFraction(grandTotal, true, true)}</TableItemRight>
                </tr>
                <tr>
                  <td colSpan={2}>
                    <SimplePricePreview price={grandTotal} />
                  </td>
                </tr>
                <tr>
                  <td>Estimated reward point</td>
                  <TableItemRightPoint>
                    {estimatedEarnPoint} P ({estimatedEarnPoint == 0 ? 0 : pointRateValue}%)
                  </TableItemRightPoint>
                </tr>
              </tbody>
            </Table>
          </Container>
        </CheckoutBody>
      </CheckoutCard>

      <Container>
        <Row>
          <ContinueButtonWrapperCol>
            <Button cName="continue-btn" variant="contained" disabled={disabled} onClick={handleClickContinue}>
              Continue
            </Button>
          </ContinueButtonWrapperCol>
        </Row>
      </Container>
      {/*TODO: Call setCartQuantity when disposing cart*/}
    </div>
  );
};

export { CheckoutSummary };
